import {
    fetchAuthSession,
    fetchUserAttributes,
    signOut,
} from "aws-amplify/auth"
import type { NavigateFunction } from "react-router-dom"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"

import { BrowserStorageType } from "@front-shared/config/general.config.ts"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { GENERAL_CONFIG } from "../config/general.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { AuthService } from "../services/auth.service"
import { authReset } from "../store/slices/auth.slice"
import { billerGroupsReset } from "../store/slices/billerGroups.slice"
import { checkerReset } from "../store/slices/checker.slice"
import { coverageOverridesReset } from "../store/slices/coverageOverrides.slice"
import { coveragePayersReset } from "../store/slices/coveragePayers.slice"
import { organizationReset } from "../store/slices/organization.slice"
import { policiesReset } from "../store/slices/policies.slice"
import { runtimeReset } from "../store/slices/runtime.slice"
import { supportReset } from "../store/slices/support.slice"
import { userDetailsReset } from "../store/slices/userDetails.slice"
import type { AppDispatch } from "../store/store.ts"
import { runtimeCheckUserIsLoggedIn } from "../store/thunks/runtime.thunks"
import type { IEagleEyeViewAsConfig } from "../types/objects.types.ts"
import { AmplitudeHelper } from "./amplitude.helper"
import { SocketsHelper } from "./sockets.helper"
import { UtilHelper } from "./util.helper"

export class AuthHelper {
    /**
     * Check if user is logged in
     */
    static async isLoggedIn(): Promise<boolean> {
        return !!(await AuthHelper.getAccessToken())
    }

    /**
     * Get access token from Amplify session
     * Always returns access token (not ID token) for both:
     * - Regular Cognito users (password authentication)
     * - Federated users (SSO/identity provider authentication)
     */
    static async getAccessToken(): Promise<string | undefined> {
        try {
            const session = await fetchAuthSession()

            // Explicitly use access token for all user types, including federated users
            return session.tokens?.accessToken?.toString()
        } catch (e) {
            return undefined
        }
    }

    /**
     * Process user after successful login
     */
    static async processUserSuccessLoggedIn(
        navigate?: NavigateFunction,
    ): Promise<boolean> {
        try {
            const jwtToken = await AuthHelper.getAccessToken()

            if (!jwtToken) {
                ToastrHelper.error("Could not authenticate user")

                return false
            }

            BrowserStorageHelper.set(
                GENERAL_CONFIG.browserStorageKeys.needTrackLoggedInEvent,
                true,
                BrowserStorageType.sessionStorage,
            )

            // Handle redirectTo from url query
            UtilHelper.redirectTo(
                UtilHelper.getRawUrlSearchParam(
                    GENERAL_CONFIG.urlSearchParamsKeys.redirectTo,
                ) || ROUTES_CONFIG.baseApplicationUrl,
                navigate,
            )

            setTimeout(() => {
                UtilHelper.checkAppVersionUpdateNeeded()
            })

            return true
        } catch (e) {
            return false
        }
    }

    /**
     * Logout user
     */
    static logout(
        dispatch: AppDispatch | null,
        redirectUrl: string = ROUTES_CONFIG.loginUrl,
        navigate?: NavigateFunction,
        needForceResetMFA = false,
    ) {
        signOut()
            .then(() => {
                AuthService.clearAuthCache()

                AmplitudeHelper.clearUser()

                try {
                    sessionStorage.clear()

                    // Since we are clearing all session storage, we need to keep force MFA reset variable if it's set
                    if (needForceResetMFA) {
                        BrowserStorageHelper.set(
                            GENERAL_CONFIG.browserStorageKeys.authForceResetMFA,
                            true,
                            BrowserStorageType.sessionStorage,
                        )
                    }
                } catch (e) {}

                // Clear saved filters in local storage
                try {
                    Object.values(PoliciesListType).forEach(viewType => {
                        BrowserStorageHelper.remove(
                            viewType +
                                GENERAL_CONFIG.browserStorageKeys
                                    .policiesFiltersConfigurationSuffix,
                            BrowserStorageType.localStorage,
                        )
                    })
                } catch (e) {}

                // Clear saved sort in local storage
                try {
                    Object.values(PoliciesListType).forEach(viewType => {
                        BrowserStorageHelper.remove(
                            viewType +
                                GENERAL_CONFIG.browserStorageKeys
                                    .policiesSortConfigurationSuffix,
                            BrowserStorageType.localStorage,
                        )
                    })
                } catch (e) {}

                AuthHelper.removeEagleEyeViewAsData(false)

                // Clear socket.io connection
                const socketInstance = SocketsHelper.getInstance()
                socketInstance.disconnect(true)

                if (dispatch) {
                    dispatch(authReset())
                    dispatch(billerGroupsReset())
                    dispatch(checkerReset())
                    dispatch(coverageOverridesReset())
                    dispatch(coveragePayersReset())
                    dispatch(organizationReset())
                    dispatch(policiesReset())
                    dispatch(runtimeReset())
                    dispatch(supportReset())
                    dispatch(userDetailsReset())

                    dispatch(runtimeCheckUserIsLoggedIn())
                }

                // If no dispatch, we will just redirect with reload
                UtilHelper.redirectTo(
                    redirectUrl,
                    dispatch ? navigate : undefined,
                )
            })
            .catch(e => {
                ToastrHelper.error(e.message)
            })
    }

    /**
     * Set view as data for Eagle Eye functionality
     * that allows to view as any other user and view any practice that that user has access to
     */
    static setEagleEyeViewAsData(
        data: IEagleEyeViewAsConfig,
        withReload = true,
    ) {
        BrowserStorageHelper.set(
            GENERAL_CONFIG.browserStorageKeys.adminViewAsKey,
            data,
            BrowserStorageType.localStorage,
        )

        if (withReload) {
            window.location.reload()
        }
    }

    /**
     * Get view as data for Eagle Eye functionality
     */
    static getEagleEyeViewAsData(): IEagleEyeViewAsConfig | null {
        return BrowserStorageHelper.get(
            GENERAL_CONFIG.browserStorageKeys.adminViewAsKey,
            null,
            BrowserStorageType.localStorage,
        )
    }

    /**
     * Delete view as data for Eagle Eye functionality
     */
    static removeEagleEyeViewAsData(withReload = true) {
        BrowserStorageHelper.remove(
            GENERAL_CONFIG.browserStorageKeys.adminViewAsKey,
            BrowserStorageType.localStorage,
        )

        if (withReload) {
            window.location.reload()
        }
    }

    /**
     * Get phone number that is used for MFA
     */
    static async getMFAPhoneNumber(ignoreCache = true): Promise<string | null> {
        try {
            const attributes = ignoreCache
                ? await fetchUserAttributes()
                : (await AuthService.getAuthCache()).fetchedUserAttributes

            return attributes.phone_number_verified === "true"
                ? attributes.phone_number || null
                : null
        } catch (e) {
            return null
        }
    }
}
